<template>
  <div :class="cn('flex', classes?.root, props.class)">
    <picture
      v-bind="attributes?.picture"
      :class="cn('flex-1', classes?.picture)"
      :style="{ backgroundColor: bgColor }"
    >
      <source
        v-if="mobileSrc"
        v-bind="attributes?.source"
        :media="`(max-width: ${AppBreakpoints.md}px)`"
        :srcset="getImage(mobileSrc)"
        type="image/webp"
      />

      <NuxtImg
        v-bind="attributes?.image"
        :key="src"
        :class="
          cn(
            'h-full w-full object-cover opacity-0 transition-opacity duration-1000 ease-out',
            { 'opacity-100': imageLoaded },
            props.class,
            classes?.image
          )
        "
        :alt
        :preload
        quality="100"
        format="webp"
        loading="lazy"
        :src="getImageUrl(src)"
        @load="imageLoaded = true"
      />
    </picture>
  </div>
</template>

<script setup lang="ts">
import type { NativeElements } from "vue"

import { NuxtImg } from "#components"
import type { ComponentProps } from "vue-component-type-helpers"

import { AppBreakpoints } from "@finq/app-base/lib/configs"

import { getImageUrl } from "@finq/ui/composables/useImage"
import { cn } from "@finq/ui/utils/functions/tailwindMerge"

export interface ResponsiveImageClasses {
  root?: ClassValue
  picture?: ClassValue
  image?: ClassValue
}

export interface ResponsiveImageAttributes {
  picture?: NativeElements["picture"]
  source?: NativeElements["source"]
  image?: Omit<ComponentProps<typeof NuxtImg>, "src" | "alt" | "class">
}

export interface ResponsiveImageProps {
  class?: ClassValue
  src: string
  alt: string
  mobileSrc?: string
  bgColor?: string
  classes?: Partial<ResponsiveImageClasses>
  attributes?: Partial<ResponsiveImageAttributes>
  preload?: boolean
}

const props = defineProps<ResponsiveImageProps>()

const imageLoaded = ref(props.preload ?? false)

const getUsingNuxtImage = useImage()
const getImage = (src: string) => getUsingNuxtImage(getImageUrl(src), { format: "webp", quality: 100 })

watch(
  () => props.src,
  () => {
    imageLoaded.value = false
  }
)
</script>
